import { useState, useEffect, useCallback, useRef } from 'react';
import { getUserOrganization } from '@/lib/firebase/queries/organizations';
import { clearSharedCustomerOrganizations, sharedCustomerOrganizations } from '@/lib/session/sessionPrefetch';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import type { Organization } from '@/types/organization';

const SELECTED_ORGANIZATION_EVENT = 'namat:selected-organization';

interface SelectedOrganizationEventDetail {
  userId: string;
  organizationId: string;
}

export function useOrganization() {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectedOrganization, setSelectedOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  // Whose organizations are loaded (null = signed out). `loading` alone lagged
  // one render behind a sign-in: guards saw «done, no organization», mounted
  // the page, then flipped back to loading and unmounted it.
  const [loadedFor, setLoadedFor] = useState<string | null | undefined>(undefined);
  const { currentUser } = useAuth();
  const fetchingRef = useRef(false);

  const fetchOrganizations = useCallback(async () => {
    console.log('🏢 useOrganization: fetchOrganizations called', { currentUser: currentUser?.uid, fetching: fetchingRef.current });
    
    if (!currentUser?.uid) {
      console.log('🏢 useOrganization: No current user, setting null');
      setOrganizations([]);
      setSelectedOrganization(null);
      setLoading(false);
      setLoadedFor(null);
      return;
    }

    if (fetchingRef.current) {
      console.log('🏢 useOrganization: Already fetching, skipping');
      return;
    }

    try {
      fetchingRef.current = true;
      setLoading(true);
      console.log('🏢 useOrganization: Fetching organizations for user:', currentUser.uid);
      
      // Get all organizations for the user
      // Shared with the other instances and started at sign-in (sessionPrefetch).
      const allOrgs = await sharedCustomerOrganizations(currentUser.uid);
      console.log('🏢 useOrganization: Found organizations:', allOrgs);
      setOrganizations(allOrgs);
      
      // Get the selected organization from localStorage or use the first one
      const savedOrgId = localStorage.getItem(`selectedOrganization_${currentUser.uid}`);
      let selectedOrg = allOrgs.length > 0 ? allOrgs[0] : null;
      
      if (savedOrgId && allOrgs.length > 0) {
        const savedOrg = allOrgs.find(org => org.id === savedOrgId);
        if (savedOrg) {
          selectedOrg = savedOrg;
        }
      }
      
      console.log('🏢 useOrganization: Setting selected organization:', selectedOrg);
      setSelectedOrganization(selectedOrg);
    } catch (error) {
      console.error('🏢 useOrganization: Error fetching organizations:', error);
      toast.error('Failed to load organization data');
    } finally {
      setLoading(false);
      setLoadedFor(currentUser.uid);
      fetchingRef.current = false;
    }
  }, [currentUser?.uid]);

  // An explicit refresh reads again instead of the shared result.
  const refreshOrganizations = useCallback(() => {
    clearSharedCustomerOrganizations();
    return fetchOrganizations();
  }, [fetchOrganizations]);

  const switchOrganization = useCallback((orgId: string) => {
    setOrganizations(currentOrgs => {
      const org = currentOrgs.find(o => o.id === orgId);
      if (org) {
        setSelectedOrganization(org);
        if (currentUser?.uid) {
          localStorage.setItem(`selectedOrganization_${currentUser.uid}`, orgId);
          // Every useOrganization() instance (navigation, route guards, widgets)
          // must follow the same active organization.
          window.dispatchEvent(
            new CustomEvent<SelectedOrganizationEventDetail>(SELECTED_ORGANIZATION_EVENT, {
              detail: { userId: currentUser.uid, organizationId: orgId },
            })
          );
        }
      }
      return currentOrgs; // Return unchanged to avoid unnecessary re-renders
    });
  }, [currentUser?.uid]);

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  // Follow organization switches made by other instances (same tab) or tabs.
  useEffect(() => {
    const uid = currentUser?.uid;
    if (!uid) return;
    const follow = (orgId: string | null) => {
      if (!orgId) return;
      setOrganizations(currentOrgs => {
        const org = currentOrgs.find(o => o.id === orgId);
        if (org) {
          setSelectedOrganization(prev => (prev?.id === org.id ? prev : org));
        }
        return currentOrgs;
      });
    };
    const onSelected = (event: Event) => {
      const detail = (event as CustomEvent<SelectedOrganizationEventDetail>).detail;
      if (detail?.userId === uid) follow(detail.organizationId);
    };
    const onStorage = (event: StorageEvent) => {
      if (event.key === `selectedOrganization_${uid}`) follow(event.newValue);
    };
    window.addEventListener(SELECTED_ORGANIZATION_EVENT, onSelected);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(SELECTED_ORGANIZATION_EVENT, onSelected);
      window.removeEventListener('storage', onStorage);
    };
  }, [currentUser?.uid]);

  return {
    organizations,
    organization: selectedOrganization, // Keep for backward compatibility
    selectedOrganization,
    organizationCount: organizations.length,
    hasOnlyOneOrganization: organizations.length === 1,
    hasMultipleOrganizations: organizations.length > 1,
    loading: loading || loadedFor !== (currentUser?.uid ?? null),
    switchOrganization,
    refreshOrganizations: refreshOrganizations
  };
}